// Shared helper for queued ingest uploads (CTA / Budget / Protocol).
//
// Why this exists
// ---------------
// The three admin upload UIs (CtasPage, BudgetsPage, ProtocolsPage) all
// follow the same shape: POST a multipart form to an ingest endpoint,
// then surface the result to the user. After the Ingest Job Queue was
// introduced, "surface the result" became "poll the queue until done,
// then surface the result". Putting that polling loop in one place
// keeps the three pages tiny and ensures they all behave identically
// (same toast cadence, same backoff, same cancel semantics).
//
// Contract
// --------
// `submitQueuedIngest(api, path, formData, opts)` returns a promise
// that resolves with the job's final `result` payload when the queue
// transitions the job to `done`, or rejects with the job's `error`
// string when it transitions to `failed`.
//
// `opts.onStatus(job)` is called every poll with the latest job
// snapshot — pass a callback if you want to update a toast or a
// per-row progress indicator. The optional `opts.signal` lets the
// caller abort the poll loop (e.g. modal closes).
//
// Failed jobs retry themselves on the server (1 min, then every 5 min);
// while one waits the job is `queued` with `next_retry_at`, so the poll
// keeps going. A job that is finally `failed` but `retryable` gets a
// Retry action on its error toast.

import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const POLL_INTERVAL_MS = 1500;     // 1.5s — snappy without hammering the API
const MAX_WAIT_MS = 10 * 60 * 1000; // 10 min of no progress — caps a runaway poll loop

/**
 * Poll `/admin/ingest-jobs/{job_id}` until the job is done (resolves with
 * its `result`) or failed (rejects; `err.job` holds the snapshot).
 */
export async function pollIngestJob(api, job_id, opts = {}, initialStatus = "queued") {
    let deadline = Date.now() + MAX_WAIT_MS;
    let lastJob = { job_id, status: initialStatus };
    while (true) {
        if (opts.signal?.aborted) {
            const e = new Error("Ingest poll aborted by caller");
            e.aborted = true;
            throw e;
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        try {
            const r = await api.get(`/admin/ingest-jobs/${job_id}`);
            lastJob = r.data || lastJob;
        } catch (e) {
            // Transient 5xx — keep polling unless we exceed the budget.
            if (Date.now() > deadline) {
                throw new Error(`Polling failed: ${formatApiErrorDetail(e?.response?.data?.detail) || e.message}`);
            }
            continue;
        }
        opts.onStatus?.(lastJob);
        if (lastJob.status === "done") return lastJob.result || {};
        if (lastJob.status === "failed") {
            const err = new Error(lastJob.error || "Ingest failed");
            err.job = lastJob;
            throw err;
        }
        // A self-healing retry is scheduled — wait for it to run.
        if (lastJob.next_retry_at) {
            deadline = Math.max(deadline, new Date(lastJob.next_retry_at).getTime() + MAX_WAIT_MS);
        }
        if (Date.now() > deadline) {
            throw new Error(`Ingest job timed out after ${Math.round(MAX_WAIT_MS / 60000)} min (last status: ${lastJob.status}). It keeps running — see Monitoring → Ingest Job Manager.`);
        }
    }
}

/**
 * Submit a multipart upload that returns `{job_id, ...}` and poll the
 * queue until the job finishes.
 *
 * @param {object} api          Axios instance (`@/lib/api`)
 * @param {string} path         Endpoint, e.g. `/admin/study-assets/ctas/ingest`
 * @param {FormData} formData   Form fields — `queued: "true"` is auto-appended.
 * @param {object} [opts]
 * @param {(job:object)=>void} [opts.onStatus]   Per-poll callback.
 * @param {AbortSignal}        [opts.signal]     Cancel the poll loop.
 * @returns {Promise<object>}   The job's `result` dict on success.
 */
export async function submitQueuedIngest(api, path, formData, opts = {}) {
    // Force the queued code path — the backend has both sync and
    // queued in the same endpoint so callers don't need separate
    // URLs.
    if (!formData.has("queued")) formData.append("queued", "true");

    const submit = await api.post(path, formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    const { job_id, status: initialStatus } = submit.data || {};
    if (!job_id) {
        // The endpoint didn't honor `queued=true` (e.g. dedup short-
        // circuit). Return the body as-is so the caller can render
        // the sync result.
        return submit.data;
    }
    return pollIngestJob(api, job_id, opts, initialStatus || "queued");
}

function statusToaster(tid, filename) {
    let prev = null;
    return (job) => {
        const key = `${job.status}:${job.auto_retries || 0}:${job.attempts || 0}`;
        if (key === prev) return;
        prev = key;
        if (job.status === "queued" && job.next_retry_at) {
            toast.loading(
                `${filename} failed — retrying automatically (${job.auto_retries}/${job.auto_retry_max}) at ${new Date(job.next_retry_at).toLocaleTimeString()}`,
                { id: tid, description: (job.last_error || "").slice(0, 160) },
            );
        } else if (job.status === "queued") {
            const pos = job.position_in_queue;
            toast.loading(pos ? `Queued for ${filename} · position #${pos}` : `Queued for ${filename}`, { id: tid });
        } else if (job.status === "processing") {
            toast.loading(job.attempts > 1 ? `Processing ${filename} (attempt ${job.attempts})…` : `Processing ${filename}…`,
                { id: tid, description: "" });
        }
    };
}

function finishToast(result, tid, opts) {
    const dup = result?._deduplicated || result?.deduplicated;
    toast.success(
        dup ? `${opts.filename} was already ingested — showing existing record.` : `${opts.filename} ingested.`,
        { id: tid, description: dup ? result?.dedup_message || "" : "" },
    );
    opts.onDone?.(result);
    return result;
}

function failToast(api, e, tid, opts) {
    if (e.aborted) {
        toast.dismiss(tid);
        return;
    }
    const job = e.job;
    const extra = job?.retryable
        ? { duration: 30000, action: { label: "Retry", onClick: () => retryWithToast(api, job, opts) } }
        : job ? { description: "The upload is no longer on the server — upload the document again." } : {};
    toast.error(e.message || `Failed to ingest ${opts.filename}`, { id: tid, ...extra });
}

/** Retry a failed job (resumes from its saved text / stored upload) and follow it with a toast. */
export async function retryWithToast(api, job, opts) {
    const tid = toast.loading(`Retrying ${opts.filename}…`);
    try {
        const { data } = await api.post(`/admin/ingest-jobs/${job.job_id}/retry`);
        const result = await pollIngestJob(api, job.job_id, { onStatus: statusToaster(tid, opts.filename) }, data?.status);
        return finishToast(result, tid, opts);
    } catch (e) {
        if (e.response) e.message = formatApiErrorDetail(e.response.data?.detail) || e.message;
        failToast(api, e, tid, opts);
        return null;
    }
}

/**
 * Convenience wrapper: drives a single toast through the queued
 * lifecycle (queued → processing → done/failed, incl. automatic
 * retries). Use when the caller just wants a "fire and forget" UX
 * with a single status indicator.
 *
 * @param {object} api
 * @param {string} path
 * @param {FormData} formData
 * @param {object} opts
 * @param {string} opts.filename       Label for the toast.
 * @param {string} [opts.queuedLabel]  Override the initial toast text.
 * @param {(result:object)=>void} [opts.onDone]
 */
export async function uploadWithToast(api, path, formData, opts) {
    const tid = toast.loading(opts.queuedLabel || `Uploading ${opts.filename}…`);
    try {
        const result = await submitQueuedIngest(api, path, formData, {
            signal: opts.signal,
            onStatus: statusToaster(tid, opts.filename),
        });
        return finishToast(result, tid, opts);
    } catch (e) {
        failToast(api, e, tid, opts);
        if (e.aborted) return null;
        throw e;
    }
}
